import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import enterpriseApi from '../../../store/reducers/enterprise/enterpriseApi'
import { Enterprise2 } from '../../../interfaces/enterprise/enterprise_2'
import { isValidList } from '../../../common_functions'
import SkeletonLoading from '../../common/SkeletonLoading'
import IconLoading from '../../../assets/loading-gif.gif'

import BannerPatrocinado1 from '../../../assets/tests/banner1.webp'
import BannerPatrocinado2 from '../../../assets/tests/banner2.webp'
import BannerPatrocinado3 from '../../../assets/tests/banner3.webp'
import { Button } from '../../common/Button'
import { v4 } from 'uuid'
import { WarningData } from '../../WarningData'
import { LOCATION, NAME_OF_EMPTY } from '../../../constants/storageNames'
import campaignApi from '../../../store/reducers/campaigns/campaignApi'
import {
  RequestCampaignDay,
  RequestCardsByArgumentPageIndex
} from '../../../interfaces/api/request'

const SectionEnterprises = lazy(() => import('../SectionEnterprises'))
const CardPatrocinado = lazy(() => import('../../Cards/CardPatrocinado'))

interface Props {
  isHome?: boolean
}

export default function SectionSeparatedEnterprises({ isHome = false }: Props) {
  const { height, width } = useWindowDimensions()
  const [marginTop, setMarginTop] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const locationPlace = sessionStorage.getItem(LOCATION)
  const [useEnterprises] = enterpriseApi.useGetAllCardsEnterprisesMutation()
  const [useEnterprisesArgument] = enterpriseApi.useGetEnterprisesByFullTextMutation()
  const [
    useCampaigns,
    {
      data: dataCampaigns,
      isError: isErrorCampaigns,
      isSuccess: isSuccessCampaigns,
      isLoading: isLoadingCampaigns
    }
  ] = campaignApi.useGetCampaignsDayMutation()
  const [counter, setCounter] = useState(2)
  const [enterprises, setEnterprises] = useState<Enterprise2[]>()
  const [pageIndex, setPageIndex] = useState(1)
  const [isLoadingPage, setIsLoadingPage] = useState(false)
  const [isErrorPage, setIsErrorPage] = useState(false)
  const [hasMorePages, setHasMorePages] = useState(false)
  const seedRef = useRef<number | null>(null)
  const isShowingAllCardsRef = useRef(false)
  const pageSize = 6
  const id_construtora_teste = '7e850aad-29ee-47ed-bc78-648607a22666'

  const separatedList = useMemo(() => separateList<Enterprise2>(enterprises ?? []), [enterprises])

  const getEnterprises = async (page: number) => {
    const seedToSend = page > 1 ? seedRef.current : null
    const response = await useEnterprises({ pageIndex: page, pageSize, seed: seedToSend }).unwrap()
    if (page === 1) seedRef.current = response.Metadata?.Seed ?? null
    return response.Data ?? []
  }

  const getEnterprisesFullText = async (page: number) => {
    let content: RequestCardsByArgumentPageIndex = {
      argumentSearch: locationPlace ?? '',
      pageSize: pageSize,
      pageIndex: page
    }
    const response = await useEnterprisesArgument(content).unwrap()
    return response.Data ?? []
  }

  const fetchPage = async (page: number) => {
    if (page === 1) isShowingAllCardsRef.current = !isFullText()
    if (isShowingAllCardsRef.current) return getEnterprises(page)

    const cardsByLocation = await getEnterprisesFullText(page)
    const shouldFallbackToAllCards = page === 1 && cardsByLocation.length === 0
    if (!shouldFallbackToAllCards) return cardsByLocation

    isShowingAllCardsRef.current = true
    return getEnterprises(page)
  }

  const loadPage = async (page: number) => {
    setIsLoadingPage(true)
    setIsErrorPage(false)
    try {
      const cards = await fetchPage(page)
      const visibleCards = cards.filter((x) => x.ConstructionCompanyId != id_construtora_teste)
      setHasMorePages(cards.length >= pageSize)
      setEnterprises((previous) => {
        if (page === 1 || previous == undefined) return visibleCards
        const shownIds = new Set(previous.map((x) => x.Id))
        return [...previous, ...visibleCards.filter((x) => !shownIds.has(x.Id))]
      })
    } catch {
      setIsErrorPage(true)
      setHasMorePages(false)
    } finally {
      setIsLoadingPage(false)
    }
  }

  const getCampaignsDay = async () => {
    const content: RequestCampaignDay = {
      date: formatDate(new Date()),
      constructionCompanyId: null
    }
    await useCampaigns(content)
  }

  function formatDate(date: Date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // meses começam em 0
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`
  }

  useEffect(() => {
    getCampaignsDay()
  }, [])

  useEffect(() => {
    setCounter(isMobile ? 1 : 2)
    if (isMobile) {
      var canvas = document.querySelector('canvas')
      if (canvas != null) {
        canvas.remove()
      }
    }
  }, [isMobile])

  useEffect(() => {
    loadPage(pageIndex)
  }, [locationPlace, pageIndex])

  useEffect(() => {
    // if (width < 345) setMarginTop(30)
    if (width < 480) setMarginTop(20)
    else if (width < 600) setMarginTop(17)
    else setMarginTop(5)

    if (width < 769) setIsMobile(true)
    else setIsMobile(false)
  }, [width, height])

  function separateList<T>(list: T[]): T[][] {
    let result: T[][] = []
    let temp: T[] = []
    let counter = 0

    for (let i = 0; i < list.length; i++) {
      temp.push(list[i])
      counter += 1
      if (counter == 3) {
        result.push(temp)
        temp = []
        counter = 0
      } else if (i == list.length - 1) {
        result.push(temp)
      }
    }
    return result
  }

  function isFullText() {
    return locationPlace != NAME_OF_EMPTY
  }

  const getCardPatrocinado = (index: number) => {
    if (isSuccessCampaigns) {
      var campaigns = undefined
      if (index === 0) {
        campaigns = dataCampaigns?.Data.filter((x) => x.PositionSearch == 1)
      } else if (index === 1) {
        campaigns = dataCampaigns?.Data.filter((x) => x.PositionSearch == 2)
      } else if (index == 2) {
        campaigns = dataCampaigns?.Data.filter((x) => x.PositionSearch == 3)
      }

      if (campaigns != undefined && campaigns.length > 0) {
        var campaign = campaigns[0]
        return (
          <CardPatrocinado
            bannerDesktopUrl={campaign.BannerDesktopUrl}
            bannerMobileUrl={campaign.BannerMobileUrl}
            idCampaign={campaign.Id}
            enterpriseId={campaign.EnterpriseId}
            link={`/empreendimentos/${campaign.Slug}`}
          />
        )
      } else {
        return <></>
      }
    }
    // if (index % 3 === 0) {
    //   return (
    //     <CardPatrocinado
    //       bannerDesktopUrl={BannerPatrocinado1}
    //       link="https://www.mrv.com.br/imoveis/apartamentos/sao-paulo/ribeirao-preto/recreio-das-acacias/reserva-dos-lirios"
    //     />
    //   )
    // } else if (index % 3 === 1) {
    //   return <CardPatrocinado bannerDesktopUrl={BannerPatrocinado2} />
    // } else {
    //   return <CardPatrocinado bannerDesktopUrl={BannerPatrocinado3} />
    // }
  }

  const hasHiddenLoadedRows = counter < separatedList.length

  const seeMore = () => {
    setCounter(counter + 2)
    if (hasMorePages) setPageIndex(pageIndex + 1)
  }

  return (
    <>
      {isHome && (
        <>
          <h2 className="text-4xl lg:text-6xl mb-5 text-black font-bold">
            <>
              <span className="overline decoration-[#F15C00]">Baseados </span>
              na sua localização
            </>
          </h2>
          <p className='text-xl'>
          Descubra as nossas principais escolhas próximas a você.
          </p>
        </>
      )}
      <WarningData
        classNameContainer="mb-10"
        isLoading={isLoadingPage && (enterprises == undefined || pageIndex == 1)}
        isError={isErrorPage && pageIndex > 1}
        marginTop={`${isHome ? "0" : marginTop}rem`}
      />
      {isValidList(separatedList) ? (
        <>
          <Suspense fallback={<SkeletonLoading />}>
            {separatedList?.slice(0, counter).map((element: Enterprise2[], index) => {
              return (
                <>
                  <Suspense fallback={<SkeletonLoading />} key={v4()}>
                    <div style={{ marginTop: `${index == 0 ? (isHome ? "0" : marginTop) : 0}rem` }}>
                      {isMobile && (
                        <>
                          {isSuccessCampaigns && (
                            <div className="my-5">
                              <Suspense fallback={<SkeletonLoading />}>
                                {getCardPatrocinado(0)}
                              </Suspense>
                            </div>
                          )}
                          {isLoadingCampaigns && <SkeletonLoading height={100} />}
                        </>
                      )}
                      <SectionEnterprises
                        size={element.length}
                        listEnterprises={element}
                        isCarousel={isMobile}
                      />
                    </div>
                  </Suspense>
                  {!isMobile && (
                    <div className="my-5">
                      {isSuccessCampaigns && (
                        <Suspense fallback={<SkeletonLoading />}>
                          {getCardPatrocinado(index)}
                        </Suspense>
                      )}
                      {isLoadingCampaigns && <SkeletonLoading height={150} />}
                    </div>
                  )}
                </>
              )
            })}
          </Suspense>
        </>
      ) : (
        <div className={isMobile ? 'mt-[200px]' : 'mt-20'}></div>
      )}
      {(isMobile ? isValidList(enterprises) : hasMorePages || hasHiddenLoadedRows) && (
        <div className="lg:w-[250px] max-w-full m-auto mb-5">
          {isMobile ? (
            <Button
              id="seeMoreEnterprisesHome"
              variant="primary"
              className="rounded-lg w-full lg:mt-8"
              onClick={() => {
                window.location.href = '/busca'
              }}
            >
              <button>VER MAIS IMÓVEIS</button>
            </Button>
          ) : (
            <Button
              id="seeMoreEnterprisesHome"
              variant="primary"
              disabled={isLoadingPage}
              className="rounded-lg w-full lg:mt-8"
              onClick={seeMore}
            >
              <button className="w-full contents">
                VER MAIS IMÓVEIS{' '}
                {isLoadingPage && (
                  <span className="ms-2">
                    <img src={IconLoading} className="w-[20px]" />
                  </span>
                )}
              </button>
            </Button>
          )}
        </div>
      )}
      {isMobile && (
        <>
          {isSuccessCampaigns && (
            <div className="my-5">
              <Suspense fallback={<SkeletonLoading />}>{getCardPatrocinado(1)}</Suspense>
            </div>
          )}
          {isLoadingCampaigns && <SkeletonLoading height={100} />}
        </>
      )}
    </>
  )
}
